import { AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import {
  ChatbotConversation,
  type IResponseEventChatbotAppointmentBooked,
  type IChatbotConversationResponse,
  type IChatbotConversationStatus,
} from 'entities/Chatbot'
import { ChatbotConversationApi } from 'entities/Chatbot/api/conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EmojiAction, EnumVariantMessageField, MessageFieldStore } from 'widgets/MessageField'

const getErrorMessage = (error: unknown): string => {
  const commonMessage = 'Something went wrong. Please try again.'

  if (!(error instanceof AxiosError)) return commonMessage

  const payload = error.response?.data ?? {}

  if (payload.status === 'error' && typeof payload.message === 'string') return payload.message

  return commonMessage
}

export class ChatbotDialogStore {
  private _uuid: string | null = null
  private _status: IChatbotConversationStatus | null = null
  private _invalid = false
  private _started = false
  private _loading = false
  private _initialLoading = false
  private _conversation = new ChatbotConversation()
  private _message: MessageFieldStore
  shouldShowIncomingMessage = false

  constructor(private _chatbotId: string | undefined, private _entities: IEntitiesStore) {
    this._message = new MessageFieldStore(
      {
        placeholder: 'Write a message...',
        styles: {
          minHeight: 60,
        },
        variant: EnumVariantMessageField.Default,
        resetOnUnmount: false,
        textLimit: {
          maxLength: 1600,
          message: 'Message should not be longer than 1.600 characters',
        },
        makeActions: () => [
          {
            iconButtonComponent: <EmojiAction />,
          },
        ],
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  get conversation() {
    return this._conversation
  }

  get message() {
    return this._message
  }

  get started() {
    return this._started
  }

  get completed() {
    return this._status !== null && this._status !== 'in_progress'
  }

  get status() {
    return this._status
  }

  get invalid() {
    return this._invalid
  }

  get loading() {
    return this._loading
  }

  get initialLoading() {
    return this._initialLoading
  }

  invalidate = () => {
    if (!this._started) return

    this._invalid = true
  }

  start = async (isAutoConvType: boolean) => {
    if (!this._chatbotId) return false

    this._loading = true
    this._initialLoading = true

    try {
      const { data } = await ChatbotConversationApi.start(Number(this._chatbotId))
      let autoStartResponse: IChatbotConversationResponse | null = null

      if (isAutoConvType && data.session_uuid) {
        const { data: autoStartData } = await ChatbotConversationApi.autoStart(data.session_uuid)
        autoStartResponse = autoStartData
      }

      return runInAction(() => {
        this._status = autoStartResponse?.status ?? data.status
        this._uuid = autoStartResponse?.session_uuid ?? data.session_uuid
        this._conversation.addOrUpdate(autoStartResponse?.chat ?? data.chat)

        this._started = true
        this._loading = false
        this._initialLoading = false

        return true
      })
    } catch (error) {
      console.log(error)

      const message = getErrorMessage(error)

      toastStore.add({
        title: message,
        type: 'error',
      })

      return runInAction(() => {
        this._started = false
        this._loading = false
        this._initialLoading = false

        return false
      })
    }
  }

  send = async (message: string) => {
    if (!this._uuid) return false

    this._loading = true

    try {
      const { data } = await ChatbotConversationApi.message(this._uuid, message)

      return runInAction(() => {
        this._status = data.status
        this._uuid = data.session_uuid
        this._conversation.addOrUpdate(data.chat)

        this._loading = false

        return true
      })
    } catch (error) {
      console.log(error)

      const message = getErrorMessage(error)

      toastStore.add({
        title: message,
        type: 'error',
      })

      return runInAction(() => {
        this._loading = false

        return false
      })
    }
  }

  testFollowup = async () => {
    if (!this._uuid) return false

    this._loading = true
    this.shouldShowIncomingMessage = true

    try {
      const { data } = await ChatbotConversationApi.testFollowup(this._uuid)

      return runInAction(() => {
        this._status = data.status
        this._uuid = data.session_uuid
        this._conversation.addOrUpdate(data.chat)

        return true
      })
    } catch (error) {
      logger.error(error)
      const message = getErrorMessage(error)

      toastStore.add({
        title: message,
        type: 'error',
      })
      return false
    } finally {
      runInAction(() => {
        this._loading = false
        this.shouldShowIncomingMessage = false
      })
    }
  }

  checkAppointment = (data: IResponseEventChatbotAppointmentBooked) => {
    if (this._uuid === data.session_uuid) {
      this.history(data.session_uuid)
    }
  }

  history = async (uuid: string) => {
    runInAction(() => {
      this._loading = true
    })

    try {
      const { data } = await ChatbotConversationApi.getHistory(uuid)

      runInAction(() => {
        this._status = data.status
        this._uuid = data.session_uuid
        this._conversation.addOrUpdate(data.chat)

        this._loading = false
      })
    } catch (error) {
      const message = getErrorMessage(error)

      toastStore.add({
        title: message,
        type: 'error',
      })

      logger.error(message)

      runInAction(() => {
        this._loading = false
      })
    }
  }

  restart = (isAutoConvType: boolean) => {
    this._uuid = null
    this._status = null
    this._invalid = false

    this._conversation.clear()
    this._message.clear()

    return this.start(isAutoConvType)
  }

  clear = () => {
    this._uuid = null
    this._status = null
    this._invalid = false
    this._started = false
    this._loading = false
    this._initialLoading = false

    this._conversation.clear()
    this._message.clear()
  }

  dispose = () => {}
}
